import Model, { hasMany, belongsTo, attr } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import { isEmpty } from '@ember/utils'
import type TeacherSubscription from 'teacher-ui-client/models/teacher-subscription'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type SubscriptionCoordinator from 'teacher-ui-client/models/subscription-coordinator'
import type School from 'teacher-ui-client/models/school'
import type DashboardPreferences from 'teacher-ui-client/models/dashboard-preferences'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Student from 'teacher-ui-client/models/student'
import { uniqBy } from 'lodash'
import type StringTransform from 'teacher-ui-client/transforms/string'
import type BooleanTransform from 'teacher-ui-client/transforms/boolean'
import type DateTransform from 'teacher-ui-client/transforms/date'
import type LanguageTagTransform from 'teacher-ui-client/transforms/language-tag'

export default class Teacher extends Model {
  @attr<StringTransform>('string') declare firstName: string

  @attr<StringTransform>('string') declare lastName: string

  @attr<StringTransform>('string') declare email: string

  @attr<DateTransform>('date') declare emailConfirmedAt: Date

  @attr<StringTransform>('string') declare login: string

  /* a BCP47 language tag - indicates what translations to use for this user. eg 'en-au', 'en', ... */
  @attr<LanguageTagTransform>('language-tag') declare language: string

  /* An ISO 639-1 code - indicates the type of preferred language for the teacher. eg 'en', 'ko', 'zh' */
  @attr<StringTransform>('string') declare locale: string

  @attr<BooleanTransform>('boolean') declare receiveReadingStudentReports: boolean

  @attr<BooleanTransform>('boolean') declare receiveMathsStudentReports: boolean

  @attr<BooleanTransform>('boolean') declare receiveWritingStudentReports: boolean

  @belongsTo<School>('school', { async: false, inverse: 'teachers' }) declare school: School

  @hasMany<TeacherSubscription>('teacher-subscription', { async: false, inverse: 'teacher' })
  declare teacherSubscriptions: TeacherSubscription[]

  @hasMany<SchoolClass>('school-class', { async: false, inverse: 'teachers' }) declare schoolClasses: SchoolClass[]

  @hasMany<SubscriptionCoordinator>('subscription-coordinator', { async: false, inverse: 'teacher' })
  declare subscriptionCoordinators: SubscriptionCoordinator[]

  @belongsTo<DashboardPreferences>('dashboard-preferences', { async: false, inverse: 'teacher' })
  declare dashboardPreferences: DashboardPreferences | null

  get fullName() {
    return `${this.firstName} ${this.lastName}`
  }

  /**
   * Returns a boolean value if the teacher is a subco
   * @todo please refactor the property name to reflect its intend -> isSubcoFor
   *
   */
  subcoFor(subscriptionType: SubscriptionType): boolean {
    if (isEmpty(this.subscriptionCoordinators)) {
      return false
    }
    return this.subscriptionCoordinators.some((subco) => subco.product === subscriptionType)
  }

  get students() {
    return this.uniqueStudentsFromClasses(this.schoolClasses)
  }

  studentsForSubscriptionType(subscriptionType: SubscriptionType) {
    const schoolClasses = this.schoolClasses.filter((schoolClass) => schoolClass.subscriptionType === subscriptionType)
    return this.uniqueStudentsFromClasses(schoolClasses)
  }

  schoolClassesForSubscriptionType(subscriptionType: SubscriptionType) {
    return this.schoolClasses.filter((sc) => sc.subscriptionType === subscriptionType)
  }

  /**
   * Takes school classes and returns unique students in an Array
   */
  uniqueStudentsFromClasses(schoolClasses: SchoolClass[]) {
    return uniqBy(
      schoolClasses.reduce<Student[]>((acc, schoolClass) => {
        const students = schoolClass.students
        if (!students) return acc

        return [...acc, ...students]
      }, []),
      (student) => student.id,
    )
  }

  teacherSubscriptionsFor(subscriptionType: SubscriptionType) {
    return this.teacherSubscriptions.filter((item) => item.subscriptionType === subscriptionType)
  }

  hasTrialSubscriptionFor(subscriptionType: SubscriptionType) {
    return this.teacherSubscriptionsFor(subscriptionType).length > 0
  }

  latestTeacherSubscriptionFor(subscriptionType: SubscriptionType) {
    return this.teacherSubscriptionsFor(subscriptionType)?.sort((a, b) => b.endDate.getTime() - a.endDate.getTime())[0]
  }

  get isMathsTeacher() {
    return this.hasSchoolClassForSubscriptionType(SubscriptionType.Maths)
  }

  get isReadingTeacher() {
    return this.hasSchoolClassForSubscriptionType(SubscriptionType.Reading)
  }

  hasSchoolClassForSubscriptionType(subscriptionType: SubscriptionType) {
    return this.schoolClasses.some((schoolClass) => schoolClass.subscriptionType === subscriptionType)
  }

  get hasNoSchoolClasses() {
    return isEmpty(this.schoolClasses)
  }

  get schoolClassNames() {
    return this.schoolClasses.map((item) => item.name)
  }

  [Type] = 'teacher' as const
}
